import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { StatusPill } from '@/components/ui/status-pill';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

/*
  Delivery pipeline (02-dashboard idea 4): Active projects by their current stage (backend `pipeline`:
  the earliest stage In Progress / Submitted, else the first not finished), in template order. A bar has up to three
  parts: navy = the stage is in progress / submitted; light navy = "Up next" — the earlier stages are finished but
  this one has not begun (backend `queued`); grey = stage tracking never began (every stage still "Not started",
  counted at the first stage). Clicking a stage lists its projects, each linking to the project's Stages tab.
*/

/** Projects at this stage whose tracking began but whose current stage has not (older servers: from the list). */
const queuedCount = (stage) => (stage.queued !== undefined && stage.queued !== null
  ? Number(stage.queued) || 0
  : (stage.projects || []).filter((project) => project.started && /^not started$/i.test(String(project.stage_status || '').trim())).length);

const PHONE_TOP = 5;
const plural = (count, one, many = `${one}s`) => (count === 1 ? one : many);

function ProjectList({ title, projects }) {
  return (
    <>
      <p className="border-b border-line-2 px-3.5 py-2.5 text-[13px] font-bold text-ink">{title}</p>
      <ul className="max-h-[300px] overflow-y-auto py-1">
        {projects.map((project) => (
          <li key={project.id}>
            <Link
              to={`/projects/${project.id}?tab=stages`}
              className="flex items-center gap-2 px-3.5 py-2 hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none"
              data-testid="pipeline-project"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-ink">{project.name}</span>
                <span className="block truncate text-[12px] text-ink-3">
                  {project.project_engineer || 'No Project Engineer'}
                  {project.stage_number ? ` · stage ${project.stage_number} of ${project.stage_count}` : ''}
                </span>
              </span>
              <StatusPill status={project.stage_status} domain="stage" size="sm" />
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ink-4" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}

function StageRow({ stage, max }) {
  const queued = queuedCount(stage);
  const started = Math.max(0, (Number(stage.started) || 0) - queued);
  const notStarted = Number(stage.not_started) || 0;
  const count = Number(stage.count) || 0;
  const zero = count === 0;
  const rowClass = 'grid w-full grid-cols-[132px_minmax(0,1fr)_28px] items-center gap-x-2.5 rounded-md px-1.5 text-left sm:grid-cols-[164px_minmax(0,1fr)_28px] sm:gap-x-3';
  const body = (
    <>
      <span className={cn('truncate text-[13px]', zero ? 'text-ink-3' : 'text-ink-2')}>{stage.name}</span>
      <span className={cn('flex h-2 min-w-0 gap-0.5 rounded-full', !zero && 'bg-[color:var(--line-2)]')} aria-hidden="true">
        {started ? <span className="h-full min-w-[8px] rounded-full bg-[color:var(--brand)]" style={{ width: `${(started / max) * 100}%` }} /> : null}
        {queued ? (
          <span className="h-full min-w-[8px] rounded-full bg-[color:var(--navy-300)]" style={{ width: `${(queued / max) * 100}%` }} data-testid="pipeline-queued-bar" />
        ) : null}
        {notStarted ? (
          <span
            className="h-full min-w-[8px] rounded-full bg-[color:var(--status-gray-bg)] shadow-[inset_0_0_0_1px_var(--ink-4)]"
            style={{ width: `${(notStarted / max) * 100}%` }}
          />
        ) : null}
      </span>
      <span className={cn('text-right text-[13px] tabular-nums', zero ? 'font-semibold text-ink-4' : 'font-bold text-ink')}>
        {zero ? (
          <>
            <span aria-hidden="true">—</span>
            <span className="sr-only">no projects</span>
          </>
        ) : formatNumber(count, { decimals: 0 })}
      </span>
    </>
  );
  if (zero) {
    return <div className={cn(rowClass, 'h-[26px]')} data-testid="pipeline-row">{body}</div>;
  }
  const label = `${stage.name}: ${count} ${plural(count, 'project')}${queued ? `, ${queued} up next (stage not begun)` : ''}${notStarted ? `, ${notStarted} not started yet` : ''}. Show them.`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(rowClass, 'h-[30px] hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:h-[26px]')}
          aria-label={label}
          data-testid="pipeline-row"
        >
          {body}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="left" collisionPadding={12} className="w-[300px] rounded-control border-line bg-surface p-0 shadow-raised">
        <ProjectList title={`${stage.name} · ${count} active ${plural(count, 'project')}`} projects={stage.projects || []} />
      </PopoverContent>
    </Popover>
  );
}

export function PipelineCard({ pipeline, isPhone = false }) {
  if (!pipeline) return null;
  const stages = pipeline.stages || [];
  const max = Math.max(1, ...stages.map((stage) => Number(stage.count) || 0));
  const notStartedProjects = stages.flatMap((stage) => (stage.projects || []).filter((project) => !project.started));
  const queuedTotal = stages.reduce((sum, stage) => sum + queuedCount(stage), 0);
  let visible = stages;
  let note = '';
  if (isPhone) {
    const top = new Set([...stages].filter((stage) => stage.count > 0).sort((a, b) => b.count - a.count).slice(0, PHONE_TOP));
    visible = stages.filter((stage) => top.has(stage));
    const restWith = stages.filter((stage) => !top.has(stage) && stage.count > 0);
    const empty = stages.filter((stage) => !stage.count).length;
    note = [
      ...restWith.map((stage) => `${stage.name} ${stage.count}`),
      empty ? `${empty} ${plural(empty, 'stage')} empty` : '',
    ].filter(Boolean).join(' · ');
  }

  return (
    <section className="min-w-0 overflow-hidden rounded-card border border-line bg-surface shadow-card" aria-labelledby="pipeline-title" data-testid="pipeline-card">
      <div className="flex items-start justify-between gap-3 border-b border-line-2 px-4 py-3.5 sm:px-5">
        <div className="min-w-0">
          <h2 id="pipeline-title" className="text-[15px] font-bold text-ink">Delivery pipeline</h2>
          <p className="mt-0.5 text-[12.5px] text-ink-3">Active projects by current stage{isPhone && note ? ` · top ${visible.length}` : ''}</p>
        </div>
        <span className="shrink-0 rounded-full border border-line px-2 py-0.5 text-[12px] font-semibold text-ink-2">
          {formatNumber(pipeline.active || 0, { decimals: 0 })} active
        </span>
      </div>
      {stages.length === 0 ? (
        <p className="px-5 py-4 text-[13px] text-ink-3">No active projects have stages yet.</p>
      ) : (
        <div className="px-3 pb-3.5 pt-2.5 sm:px-3.5">
          <div className="flex flex-col" role="list" aria-label="Active projects by current stage">
            {visible.map((stage) => (
              <div role="listitem" key={stage.name}>
                <StageRow stage={stage} max={max} />
              </div>
            ))}
          </div>
          {note ? <p className="px-1.5 pt-1.5 text-[12px] leading-snug text-ink-3">+ {note}</p> : null}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 px-1.5 text-[12px] font-semibold text-ink-3">
            <span className="inline-flex min-h-[28px] items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-[color:var(--brand)]" aria-hidden="true" />
              In progress / submitted
            </span>
            {queuedTotal ? (
              <span
                className="inline-flex min-h-[28px] items-center gap-1.5"
                title="Earlier stages are finished; this stage has not begun yet"
                data-testid="pipeline-legend-queued"
              >
                <span className="h-2 w-2 rounded-full bg-[color:var(--navy-300)]" aria-hidden="true" />
                Up next · {formatNumber(queuedTotal, { decimals: 0 })}
              </span>
            ) : null}
            {pipeline.not_started ? (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex min-h-[28px] items-center gap-1.5 rounded text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid="pipeline-not-started"
                  >
                    <span className="h-2 w-2 rounded-full bg-[color:var(--status-gray-bg)] shadow-[inset_0_0_0_1px_var(--ink-4)]" aria-hidden="true" />
                    Not started yet · {formatNumber(pipeline.not_started, { decimals: 0 })}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" collisionPadding={12} className="w-[300px] rounded-control border-line bg-surface p-0 shadow-raised">
                  <ProjectList
                    title={`${pipeline.not_started} ${plural(pipeline.not_started, 'project has', 'projects have')} not started stage tracking`}
                    projects={notStartedProjects}
                  />
                </PopoverContent>
              </Popover>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}
